import React, { useState, useEffect, useMemo } from "react";
import {
  Users,
  AlertCircle,
  Shield,
  MapPin,
  Plus,
  Search,
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  Moon,
  FileWarning,
  Smartphone,
  Radio,
  Radar,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import axiosInstance from "../../../common/axiosInstance";
import {
  getAllThreatReports,
  createThreatReport,
  updateThreatReportStatus,
  getAllNightShiftCheckIns,
  createNightShiftCheckIn,
  performCheckIn,
  performCheckOut,
  confirmSafety,
  ThreatReport,
  ThreatReportFormData,
  NightShiftCheckIn,
  NightShiftCheckInFormData,
} from "../../../service/securityService";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import PanicMobileTab from "./PanicMobileTab";
import LoneWorkerTab from "./LoneWorkerTab";
import GeoFenceTab from "./GeoFenceTab";

interface SafetyEvent {
  id: string;
  staffId: string;
  staffName: string;
  eventType: string;
  location: string;
  timestamp: string;
  status: string;
  description: string;
}

const StaffSafety: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<
    | "events"
    | "panic"
    | "loneworker"
    | "geofence"
    | "threats"
    | "nightshift"
  >("events");
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [threatReports, setThreatReports] = useState<ThreatReport[]>([]);
  const [nightShiftCheckIns, setNightShiftCheckIns] = useState<
    NightShiftCheckIn[]
  >([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL Regions");
  const [selectedPremises, setSelectedPremises] =
    useState<string>("ALL Premises");
  const [searchQuery, setSearchQuery] = useState("");

  // Modal states
  const [isThreatModalOpen, setIsThreatModalOpen] = useState(false);
  const [isNightShiftModalOpen, setIsNightShiftModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState<ThreatReport | null>(
    null
  );
  const [zones, setZones] = useState<Array<{ value: string; label: string }>>(
    []
  );

  // Form data
  const [threatFormData, setThreatFormData] = useState<ThreatReportFormData>({
    threatType: "Suspicious Activity",
    description: "",
    location: "",
    zone: "",
    isAnonymous: false,
    severity: "Medium",
  });

  const [nightShiftFormData, setNightShiftFormData] =
    useState<NightShiftCheckInFormData>({
      staffId: "",
      staffName: "",
      department: "",
      shiftDate: new Date().toISOString().slice(0, 10),
      shiftStart: "22:00",
      shiftEnd: "06:00",
      location: "",
      zone: "",
    });

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    if (activeTab === "events") {
      fetchSafetyEvents();
    } else if (activeTab === "threats") {
      fetchThreatReports();
    } else if (activeTab === "nightshift") {
      fetchNightShiftCheckIns();
    }
  }, [selectedRegion, selectedPremises, searchQuery, activeTab]);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get("/api/security/access-zones");
      if (response.data.success) {
        const zonesData = response.data.data || response.data.message || [];
        setZones(
          zonesData.map((zone: any) => ({
            value: zone._id || zone.id,
            label: zone.zoneName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const fetchSafetyEvents = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        limit: 100,
        sort: "incidentDateTime DESC",
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await axiosInstance.get("/api/security/incidents", {
        params,
      });

      const incidents = response.data?.data ?? [];

      const matchSafety = (i: any) => {
        const t = (i.incidentType || "").toLowerCase();
        if (i.isPanicButton) return true;
        if (t.includes("geo")) return true;
        if (t.includes("violence") || t.includes("assault")) return true;
        if (t.includes("threat")) return true;
        if (t.includes("panic")) return true;
        return false;
      };

      const filtered = incidents.filter(matchSafety);
      const list = filtered.length > 0 ? filtered : incidents.slice(0, 40);

      const safetyEvents: SafetyEvent[] = list.map(
        (incident: any, index: number) => ({
          id: String(incident._id || incident.id || `safety-${index}`),
          staffId: String(incident.reportedBy || ""),
          staffName:
            incident.reportedByName ||
            incident.reportedBy ||
            "Security / system",
          eventType: incident.isPanicButton
            ? `Panic (${incident.panicSource || "mobile"})`
            : incident.incidentType || "Incident",
          location: incident.location || "—",
          timestamp:
            incident.incidentDateTime ||
            incident.createdAt ||
            new Date().toISOString(),
          status: incident.status || "Open",
          description: incident.description || "",
        })
      );

      setEvents(safetyEvents);
    } catch (error: any) {
      console.error("Error fetching staff safety events:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to load staff safety events"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchThreatReports = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery) filters.searchQuery = searchQuery;

      const response = await getAllThreatReports(filters, { limit: 100 });
      setThreatReports(response.data || []);
    } catch (error: any) {
      console.error("Error fetching threat reports:", error);
      // Don't show error toast for 404 (endpoint may not exist yet)
      if (error.response?.status !== 404) {
        toast.error("Failed to load threat reports");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNightShiftCheckIns = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (selectedRegion !== "ALL Regions") filters.region = selectedRegion;
      if (selectedPremises !== "ALL Premises")
        filters.premise = selectedPremises;
      if (searchQuery) filters.searchQuery = searchQuery;

      const response = await getAllNightShiftCheckIns(filters, { limit: 100 });
      setNightShiftCheckIns(response.data || []);
    } catch (error: any) {
      console.error("Error fetching night shift check-ins:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to load night shift check-ins");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThreatReport = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createThreatReport(threatFormData);
      toast.success(
        threatFormData.isAnonymous
          ? "Anonymous threat report submitted successfully"
          : "Threat report submitted successfully"
      );
      setIsThreatModalOpen(false);
      resetThreatForm();
      fetchThreatReports();
    } catch (error: any) {
      console.error("Error creating threat report:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to submit threat report"
      );
    }
  };

  const handleCreateNightShiftCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createNightShiftCheckIn(nightShiftFormData);
      toast.success("Night shift check-in scheduled successfully");
      setIsNightShiftModalOpen(false);
      resetNightShiftForm();
      fetchNightShiftCheckIns();
    } catch (error: any) {
      console.error("Error creating night shift check-in:", error);
      toast.error(
        error.response?.data?.resMessage ||
        "Failed to schedule night shift check-in"
      );
    }
  };

  const handleCheckIn = async (id: string) => {
    try {
      await performCheckIn(id);
      toast.success("Check-in recorded successfully");
      fetchNightShiftCheckIns();
    } catch (error: any) {
      toast.error("Failed to record check-in");
    }
  };

  const handleCheckOut = async (id: string) => {
    try {
      await performCheckOut(id);
      toast.success("Check-out recorded successfully");
      fetchNightShiftCheckIns();
    } catch (error: any) {
      toast.error("Failed to record check-out");
    }
  };

  const handleConfirmSafety = async (id: string) => {
    try {
      await confirmSafety(id);
      toast.success("Safety confirmation recorded");
      fetchNightShiftCheckIns();
    } catch (error: any) {
      toast.error("Failed to confirm safety");
    }
  };

  const handleUpdateThreatStatus = async (id: string, status: string) => {
    try {
      await updateThreatReportStatus(id, status);
      toast.success("Threat report status updated");
      fetchThreatReports();
      setIsViewModalOpen(false);
    } catch (error: any) {
      toast.error("Failed to update status");
    }
  };

  const resetThreatForm = () => {
    setThreatFormData({
      threatType: "Suspicious Activity",
      description: "",
      location: "",
      zone: "",
      isAnonymous: false,
      severity: "Medium",
    });
  };

  const resetNightShiftForm = () => {
    setNightShiftFormData({
      staffId: "",
      staffName: "",
      department: "",
      shiftDate: new Date().toISOString().slice(0, 10),
      shiftStart: "22:00",
      shiftEnd: "06:00",
      location: "",
      zone: "",
    });
  };

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      Low: "bg-green-100 text-green-800",
      Medium: "bg-yellow-100 text-yellow-800",
      High: "bg-orange-100 text-orange-800",
      Critical: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[severity] || "bg-gray-100 text-gray-800"
          }`}
      >
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Open: "bg-red-100 text-red-800",
      Reported: "bg-blue-100 text-blue-800",
      "Under Review": "bg-yellow-100 text-yellow-800",
      Investigating: "bg-orange-100 text-orange-800",
      Resolved: "bg-green-100 text-green-800",
      Closed: "bg-gray-100 text-gray-800",
      Scheduled: "bg-blue-100 text-blue-800",
      "Checked In": "bg-green-100 text-green-800",
      "Checked Out": "bg-gray-100 text-gray-800",
      Missed: "bg-red-100 text-red-800",
      Late: "bg-orange-100 text-orange-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  const getSafetyStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Safe: "bg-green-100 text-green-800",
      Alert: "bg-orange-100 text-orange-800",
      Emergency: "bg-red-100 text-red-800",
      Unknown: "bg-gray-100 text-gray-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || "bg-gray-100 text-gray-800"
          }`}
      >
        {status}
      </span>
    );
  };

  const gridTheme = themeQuartz.withParams({
    backgroundColor: "#fff",
    foregroundColor: "#2a2a2a",
    headerTextColor: "#2a2a2a",
    headerBackgroundColor: "#E9F5FF",
    oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
    headerColumnResizeHandleColor: "rgb(126, 46, 132)",
    fontSize: "0.75rem",
    headerFontWeight: "bold",
  });

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const theme = useMemo(() => gridTheme, []);

  // Safety Events Column Definitions
  const safetyEventsColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "staffName",
        headerName: "Staff Name",
        sortable: true,
        minWidth: 150,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          fontWeight: "500",
        }),
      },
      {
        field: "eventType",
        headerName: "Event Type",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 150,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{params.value}</span>
            </div>
          );
        },
      },
      {
        field: "timestamp",
        headerName: "Timestamp",
        sortable: true,
        minWidth: 180,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => {
          if (!params.data?.timestamp) return "";
          return new Date(params.data.timestamp).toLocaleString();
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getStatusBadge(params.value || "Open");
        },
      },
    ],
    []
  );

  // Threat Reports Column Definitions
  const threatReportsColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "reportNumber",
        headerName: "Report #",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          fontWeight: "500",
        }),
        cellRenderer: (params: any) => {
          const report = params.data;
          return (
            <div className="flex items-center gap-2">
              <span>
                {report.reportNumber || report._id?.slice(-6) || "N/A"}
              </span>
              {report.isAnonymous && (
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                  Anonymous
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: "threatType",
        headerName: "Type",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{params.value}</span>
            </div>
          );
        },
      },
      {
        field: "reportedAt",
        headerName: "Reported",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => {
          if (!params.data?.reportedAt) return "";
          return new Date(params.data.reportedAt).toLocaleString();
        },
      },
      {
        field: "severity",
        headerName: "Severity",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getSeverityBadge(params.value || "Medium");
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getStatusBadge(params.value || "Reported");
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 100,
        flex: 0.8,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "center",
        }),
        cellRenderer: (params: any) => {
          const report = params.data;
          return (
            <button
              onClick={() => {
                setSelectedThreat(report);
                setIsViewModalOpen(true);
              }}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye className="w-5 h-5" />
            </button>
          );
        },
      },
    ],
    []
  );

  // Night Shift Check-ins Column Definitions
  const nightShiftColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "staffName",
        headerName: "Staff Name",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
          fontWeight: "500",
        }),
      },
      {
        field: "department",
        headerName: "Department",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => params.data?.department || "N/A",
      },
      {
        field: "shiftDate",
        headerName: "Shift Date",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => {
          if (!params.data?.shiftDate) return "";
          return new Date(params.data.shiftDate).toLocaleDateString();
        },
      },
      {
        field: "shiftTime",
        headerName: "Shift Time",
        sortable: false,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          const checkIn = params.data;
          return (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>
                {checkIn.shiftStart} - {checkIn.shiftEnd}
              </span>
            </div>
          );
        },
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 150,
        flex: 1.2,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span>{params.value}</span>
            </div>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getStatusBadge(params.value || "Scheduled");
        },
      },
      {
        field: "safetyStatus",
        headerName: "Safety",
        sortable: true,
        minWidth: 100,
        flex: 0.8,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        cellRenderer: (params: any) => {
          return getSafetyStatusBadge(params.value || "Unknown");
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "center",
        }),
        cellRenderer: (params: any) => {
          const checkIn = params.data;
          return (
            <div className="flex items-center justify-center gap-1">
              {checkIn.status === "Scheduled" && (
                <button
                  onClick={() => handleCheckIn(checkIn._id || checkIn.id || "")}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Check In"
                >
                  <CheckCircle className="w-4 h-4" />
                </button>
              )}
              {checkIn.status === "Checked In" && (
                <>
                  <button
                    onClick={() =>
                      handleConfirmSafety(checkIn._id || checkIn.id || "")
                    }
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Confirm Safety"
                  >
                    <Shield className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() =>
                      handleCheckOut(checkIn._id || checkIn.id || "")
                    }
                    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title="Check Out"
                  >
                    <Clock className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Users className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Staff Safety & Workforce Security
            </h1>
            <p className="text-gray-600 max-w-3xl">
              Panic (mobile API), lone worker sessions, night-shift welfare
              check-ins, anonymous threat reporting, and campus geo-fence
              violations — wired to Security incidents where configured.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {[
            { icon: Smartphone, t: "Panic (mobile)", d: "POST /mobile/panic-button" },
            { icon: Radio, t: "Lone worker", d: "Sessions & welfare checks" },
            { icon: Moon, t: "Night shift", d: "Scheduled check-ins" },
            { icon: FileWarning, t: "Threat reports", d: "Anonymous optional" },
            { icon: Radar, t: "Geo-fences", d: "Large campuses" },
          ].map((x) => (
            <div
              key={x.t}
              className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm text-sm"
            >
              <x.icon className="w-5 h-5 text-[#0fa9a6] mb-1" />
              <p className="font-semibold text-gray-900">{x.t}</p>
              <p className="text-xs text-gray-500">{x.d}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b">
          <button
            onClick={() => setActiveTab("events")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "events"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Safety Events
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("panic")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "panic"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Panic (mobile)
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("loneworker")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "loneworker"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4" />
              Lone worker
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("geofence")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "geofence"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Radar className="w-4 h-4" />
              Geo-fences
            </div>
          </button>
          <button
            onClick={() => setActiveTab("threats")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "threats"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              Threat Reports
            </div>
          </button>
          <button
            onClick={() => setActiveTab("nightshift")}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === "nightshift"
                ? "border-b-2 border-[#0fa9a6] text-[#0fa9a6]"
                : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <div className="flex items-center gap-2">
              <Moon className="w-4 h-4" />
              Night Shift Check-ins
            </div>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Safety feed</p>
              <p className="text-2xl font-bold text-gray-900">
                {events.length}
              </p>
            </div>
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Shield className="w-6 h-6 text-[#0fa9a6]" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Panic (in feed)</p>
              <p className="text-2xl font-bold text-red-600">
                {
                  events.filter((e) =>
                    (e.eventType || "").toLowerCase().includes("panic"),
                  ).length
                }
              </p>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <Smartphone className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Threats</p>
              <p className="text-2xl font-bold text-red-600">
                {
                  threatReports.filter(
                    (t) => t.status !== "Resolved" && t.status !== "Closed"
                  ).length
                }
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Night Shift Workers</p>
              <p className="text-2xl font-bold text-orange-600">
                {
                  nightShiftCheckIns.filter(
                    (n: any) =>
                      // Count workers who have checked in but not checked out
                      // Check status or checkInTime/checkOutTime combination
                      n.status === "Checked In" ||
                      n.status === "Late" ||
                      (n.checkInTime && !n.checkOutTime)
                  ).length
                }
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Moon className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Currently on shift</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Missed Check-ins</p>
              <p className="text-2xl font-bold text-yellow-600">
                {
                  nightShiftCheckIns.filter(
                    (n) => n.status === "Missed" || n.status === "Late"
                  ).length
                }
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Requires attention</p>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            {(activeTab === "threats" ||
              activeTab === "nightshift" ||
              activeTab === "events") && (
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={
                      activeTab === "events"
                        ? "Search incidents…"
                        : "Search…"
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>
            )}
            <div className="min-w-[150px]">
              <Dropdown
                selectedValue={selectedRegion}
                onChange={setSelectedRegion}
                type="region"
              />
            </div>
            <div className="min-w-[150px]">
              <Dropdown
                selectedValue={selectedPremises}
                onChange={setSelectedPremises}
                type="premises"
                selectedRegion={selectedRegion}
              />
            </div>
          </div>
          <div className="flex gap-2">
            {activeTab === "threats" && (
              <button
                onClick={() => setIsThreatModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Report Threat
              </button>
            )}
            {activeTab === "nightshift" && (
              <button
                onClick={() => setIsNightShiftModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0fa9a6] text-white rounded-full hover:bg-teal-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Schedule Check-in
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      <div className="bg-white rounded-lg shadow-sm">
        {activeTab === "panic" && <PanicMobileTab zones={zones} />}
        {activeTab === "loneworker" && <LoneWorkerTab zones={zones} />}
        {activeTab === "geofence" && <GeoFenceTab />}

        {activeTab === "events" && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 p-4 border-b">
              Recent Safety Events
            </h3>
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <SpinLoader />
              </div>
            ) : events.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <Shield className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  No safety events found
                </p>
                <p className="text-sm">All staff safety systems operational</p>
              </div>
            ) : (
              <div className="tableBlock w-full">
                <div className="card">
                  <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                    <AgGridReact
                      rowData={events}
                      columnDefs={safetyEventsColumnDefs}
                      theme={theme}
                      headerHeight={65}
                      rowHeight={50}
                      loading={loading}
                      defaultColDef={defaultColDef}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50]}
                      paginationAutoPageSize={false}
                      getRowStyle={() => ({
                        backgroundColor: "white",
                      })}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "threats" && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 p-4 border-b">
              Threat Reports
            </h3>
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <SpinLoader />
              </div>
            ) : threatReports.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <FileWarning className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  No threat reports found
                </p>
                <p className="text-sm">
                  Use the "Report Threat" button to submit a new report
                </p>
              </div>
            ) : (
              <div className="tableBlock w-full">
                <div className="card">
                  <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                    <AgGridReact
                      rowData={threatReports}
                      columnDefs={threatReportsColumnDefs}
                      theme={theme}
                      headerHeight={65}
                      rowHeight={50}
                      loading={loading}
                      defaultColDef={defaultColDef}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50]}
                      paginationAutoPageSize={false}
                      getRowStyle={() => ({
                        backgroundColor: "white",
                      })}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "nightshift" && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 p-4 border-b">
              Night Shift Check-ins
            </h3>
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <SpinLoader />
              </div>
            ) : nightShiftCheckIns.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <Moon className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">
                  No night shift check-ins scheduled
                </p>
                <p className="text-sm">
                  Use the "Schedule Check-in" button to add a new entry
                </p>
              </div>
            ) : (
              <div className="tableBlock w-full">
                <div className="card">
                  <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                    <AgGridReact
                      rowData={nightShiftCheckIns}
                      columnDefs={nightShiftColumnDefs}
                      theme={theme}
                      headerHeight={65}
                      rowHeight={50}
                      loading={loading}
                      defaultColDef={defaultColDef}
                      domLayout="autoHeight"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50]}
                      paginationAutoPageSize={false}
                      getRowStyle={() => ({
                        backgroundColor: "white",
                      })}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Threat Report Modal */}
      <Modal
        isOpen={isThreatModalOpen}
        onClose={() => {
          setIsThreatModalOpen(false);
          resetThreatForm();
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Report a Threat
          </h2>
          <form
            onSubmit={handleCreateThreatReport}
            id="threatForm"
            className="px-6 pb-6"
          >
            <div className="flex flex-col gap-6">
              {/* Anonymous Toggle */}
              <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <input
                  type="checkbox"
                  id="isAnonymous"
                  checked={threatFormData.isAnonymous}
                  onChange={(e) =>
                    setThreatFormData({
                      ...threatFormData,
                      isAnonymous: e.target.checked,
                    })
                  }
                  className="w-5 h-5 text-[#0fa9a6] border-gray-300 rounded focus:ring-[#0fa9a6]"
                />
                <label htmlFor="isAnonymous" className="flex flex-col">
                  <span className="font-medium text-gray-900">
                    Report Anonymously
                  </span>
                  <span className="text-sm text-gray-600">
                    Your identity will not be recorded with this report
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Threat Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={threatFormData.threatType}
                    onChange={(e) =>
                      setThreatFormData({
                        ...threatFormData,
                        threatType: e.target.value as any,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  >
                    <option value="Physical Threat">Physical Threat</option>
                    <option value="Verbal Abuse">Verbal Abuse</option>
                    <option value="Harassment">Harassment</option>
                    <option value="Suspicious Person">Suspicious Person</option>
                    <option value="Suspicious Activity">
                      Suspicious Activity
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={threatFormData.severity}
                    onChange={(e) =>
                      setThreatFormData({
                        ...threatFormData,
                        severity: e.target.value as any,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={threatFormData.location}
                    onChange={(e) =>
                      setThreatFormData({
                        ...threatFormData,
                        location: e.target.value,
                      })
                    }
                    placeholder="Enter location"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Zone
                  </label>
                  <select
                    value={threatFormData.zone}
                    onChange={(e) =>
                      setThreatFormData({
                        ...threatFormData,
                        zone: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  >
                    <option value="">Select zone</option>
                    {zones.map((zone) => (
                      <option key={zone.value} value={zone.value}>
                        {zone.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={threatFormData.description}
                  onChange={(e) =>
                    setThreatFormData({
                      ...threatFormData,
                      description: e.target.value,
                    })
                  }
                  rows={4}
                  placeholder="Describe the threat in detail..."
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  required
                />
              </div>
            </div>
          </form>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsThreatModalOpen(false);
              resetThreatForm();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="threatForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Submit Report
          </button>
        </div>
      </Modal>

      {/* Night Shift Check-in Modal */}
      <Modal
        isOpen={isNightShiftModalOpen}
        onClose={() => {
          setIsNightShiftModalOpen(false);
          resetNightShiftForm();
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            Schedule Night Shift Check-in
          </h2>
          <form
            onSubmit={handleCreateNightShiftCheckIn}
            id="nightShiftForm"
            className="px-6 pb-6"
          >
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Staff Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={nightShiftFormData.staffName}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        staffName: e.target.value,
                      })
                    }
                    placeholder="Enter staff name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Staff ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={nightShiftFormData.staffId}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        staffId: e.target.value,
                      })
                    }
                    placeholder="Enter staff ID"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <input
                    type="text"
                    value={nightShiftFormData.department}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        department: e.target.value,
                      })
                    }
                    placeholder="Enter department"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Shift Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={nightShiftFormData.shiftDate}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        shiftDate: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Shift Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={nightShiftFormData.shiftStart}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        shiftStart: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Shift End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={nightShiftFormData.shiftEnd}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        shiftEnd: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={nightShiftFormData.location}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        location: e.target.value,
                      })
                    }
                    placeholder="Enter location"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                    required
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">
                    Zone
                  </label>
                  <select
                    value={nightShiftFormData.zone}
                    onChange={(e) =>
                      setNightShiftFormData({
                        ...nightShiftFormData,
                        zone: e.target.value,
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
                  >
                    <option value="">Select zone</option>
                    {zones.map((zone) => (
                      <option key={zone.value} value={zone.value}>
                        {zone.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsNightShiftModalOpen(false);
              resetNightShiftForm();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="nightShiftForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Schedule Check-in
          </button>
        </div>
      </Modal>

      {/* View Threat Details Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedThreat(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-lg rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        {selectedThreat && (
          <>
            <div className="pt-6 overflow-y-auto flex-1">
              <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
                Threat Report Details
              </h2>
              <div className="px-6 pb-6">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Report #</p>
                      <p className="font-semibold">
                        {selectedThreat.reportNumber ||
                          selectedThreat._id?.slice(-6)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {selectedThreat.isAnonymous && (
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                          Anonymous
                        </span>
                      )}
                      {getSeverityBadge(selectedThreat.severity)}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Threat Type</p>
                    <p className="font-medium">{selectedThreat.threatType}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{selectedThreat.location}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Reported At</p>
                    <p className="font-medium">
                      {new Date(selectedThreat.reportedAt).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Description</p>
                    <p className="font-medium">{selectedThreat.description}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Current Status</p>
                    <div className="mt-1">
                      {getStatusBadge(selectedThreat.status)}
                    </div>
                  </div>

                  {selectedThreat.actionTaken && (
                    <div>
                      <p className="text-sm text-gray-500">Action Taken</p>
                      <p className="font-medium">
                        {selectedThreat.actionTaken}
                      </p>
                    </div>
                  )}

                  <div className="border-t pt-4 mt-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Update Status
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Under Review",
                        "Investigating",
                        "Resolved",
                        "Closed",
                      ].map((status) => (
                        <button
                          key={status}
                          onClick={() =>
                            handleUpdateThreatStatus(
                              selectedThreat._id || selectedThreat.id || "",
                              status
                            )
                          }
                          disabled={selectedThreat.status === status}
                          className={`px-3 py-1.5 text-sm rounded-full transition-colors ${selectedThreat.status === status
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-[#EAFBFA] text-[#0fa9a6] hover:bg-[#0fa9a6] hover:text-white"
                            }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="h-12" />
            <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedThreat(null);
                }}
              >
                Close
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default StaffSafety;
